import * as React from "react";
import { DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "@src/lib/utils/cn";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import SearchIcon from "@src/lib/icons/18px/search";
import {
  MENU_ITEM_CLASSNAME,
  MENU_SHORTCUT_CLASSNAME,
} from "@src/lib/ui/menuStyles";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      // Borderless: it usually fills a Popover or Dialog, which draws the
      // elevated frame (border, shadow) around it.
      "flex h-full w-full flex-col overflow-hidden rounded-xl bg-bg-elevated text-text-primary",
      className,
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

type CommandDialogProps = DialogProps;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <Command className="[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input]]:h-12">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    className="flex items-center gap-2 border-b border-border-primary px-3"
    // cmdk's own marker attribute, for [cmdk-input-wrapper] selectors.
    {...{ "cmdk-input-wrapper": "" }}
  >
    <SearchIcon className="size-4 shrink-0 text-icon-tertiary" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-9 w-full bg-transparent text-s-v4 text-text-primary outline-none placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-s-v4 text-text-tertiary"
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

// The group heading is MENU_LABEL_CLASSNAME (13px / 500, tertiary) on
// cmdk's heading element. Literal, so Tailwind's scanner sees every class.
const COMMAND_GROUP_HEADING_CLASSNAME =
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs-v4 [&_[cmdk-group-heading]]:font-medium-v4 [&_[cmdk-group-heading]]:text-text-tertiary";

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-text-primary",
      COMMAND_GROUP_HEADING_CLASSNAME,
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 mb-1 h-px bg-border-primary", className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

function sanitizeCmdkValue(value: string): string {
  return value.replace(/["&<>[\]()#;:\n\r]/g, " ");
}

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, value, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    value={value ? sanitizeCmdkValue(value) : value}
    className={cn(
      MENU_ITEM_CLASSNAME,
      // cmdk highlights with aria-selected (keyboard and pointer) while
      // focus stays in the input, so the row's focus: style never applies.
      "aria-selected:bg-state-hover",
      // No row gap yet: most call sites space their checkbox / icon with
      // mr-2, which a gap would double. Pass gap-2 for new rows.
      "gap-0",
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>): React.JSX.Element => {
  return <span className={cn(MENU_SHORTCUT_CLASSNAME, className)} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
